import { observer } from 'mobx-react-lite';
import { Card } from 'primereact/card';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import type { ApplicationSdk } from '../../../core/sdk';
import type SystemSettingStore from '../stores/SystemSettingStore';

const BotPhoneNumberSettings = observer(
    ({ store, sdk }: { store: SystemSettingStore; sdk: ApplicationSdk }) => {
        const handleChangeBotPhoneNumber = async () => {
            const result = await sdk.getDialogStringInput({
                title: 'Bot-Rufnummer eingeben',
                message: 'Bitte geben Sie die Bot-Rufnummer ein',
                initialValue: store.uchatConfig.uchatBotPhoneNumber || '',
                label: 'Bot-Rufnummer',
                onConfirm: async ({ input }: { input?: string }) =>
                    store.saveUChatBotPhoneNumber(input || ''),
            });

            if (result.confirmed) {
                sdk.showSuccessToast('Bot-Rufnummer erfolgreich aktualisiert');
            } else if (result.error) {
                sdk.showErrorToast('Fehler beim Aktualisieren der Bot-Rufnummer');
            }
        };

        const currentPhoneNumber = store?.uchatConfig?.uchatBotPhoneNumber || '';

        return (
            <Card
                title="Bot-Rufnummer Konfiguration"
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div className="p-fluid">
                    <div>
                        <label
                            style={{
                                display: 'block',
                                marginBottom: '0.5rem',
                                fontWeight: 'bold',
                            }}
                        >
                            Aktuelle Bot-Rufnummer:
                        </label>
                        <p
                            style={{
                                margin: 0,
                                padding: '0.5rem',
                                backgroundColor: '#f8f9fa',
                                borderRadius: '4px',
                                fontSize: '1rem',
                                color: currentPhoneNumber ? '#333' : '#666',
                            }}
                        >
                            {currentPhoneNumber || 'Keine Rufnummer konfiguriert'}
                        </p>
                    </div>

                    <div
                        style={{
                            display: 'flex',
                            gap: '0.5rem',
                            justifyContent: 'center',
                            marginTop: '1.5rem',
                        }}
                    >
                        <Button
                            label={currentPhoneNumber ? 'Rufnummer ändern' : 'Rufnummer hinzufügen'}
                            icon={currentPhoneNumber ? 'pi pi-pencil' : 'pi pi-plus'}
                            onClick={handleChangeBotPhoneNumber}
                            className={
                                currentPhoneNumber ? 'p-button-outlined' : 'p-button-primary'
                            }
                        />
                    </div>
                </div>
            </Card>
        );
    }
);

const UChatApiKeySettings = observer(
    ({ store, sdk }: { store: SystemSettingStore; sdk: ApplicationSdk }) => {
        const handleChangeApiKey = async () => {
            const result = await sdk.getDialogStringInput({
                title: 'UChat API Key eingeben',
                message: 'Bitte geben Sie Ihren UChat API Key ein',
                initialValue: store.uchatConfig.uchatApiKey || '',
                label: 'API Key',
                onConfirm: async ({ input }: { input?: string }) =>
                    store.saveUChatApiKey(input || ''),
            });

            if (result.confirmed) {
                sdk.showSuccessToast('UChat API Key erfolgreich aktualisiert');
            } else if (result.error) {
                sdk.showErrorToast('Fehler beim Aktualisieren des UChat API Keys');
            }
        };

        const hasApiKey = store.hasUChatApiKey;

        return (
            <Card
                title="UChat API Konfiguration"
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div className="p-fluid">
                    {hasApiKey ? (
                        <div>
                            <Message
                                severity="success"
                                text="UChat API-Schlüssel ist konfiguriert"
                                style={{ marginBottom: '1.5rem' }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1rem',
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    label="API-Schlüssel ändern"
                                    icon="pi pi-pencil"
                                    onClick={handleChangeApiKey}
                                    className="p-button-outlined"
                                />
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Message
                                severity="warn"
                                text="Kein UChat API-Schlüssel konfiguriert"
                                style={{ marginBottom: '1.5rem' }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1rem',
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    label="API-Schlüssel hinzufügen"
                                    icon="pi pi-plus"
                                    onClick={handleChangeApiKey}
                                    className="p-button-primary"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        );
    }
);

const UChatPartnerApiKeySettings = observer(
    ({ store, sdk }: { store: SystemSettingStore; sdk: ApplicationSdk }) => {
        const handleChangePartnerApiKey = async () => {
            const result = await sdk.getDialogStringInput({
                title: 'UChat Partner API Key eingeben',
                message: 'Bitte geben Sie Ihren UChat Partner API Key ein',
                initialValue: store.uchatConfig.uchatPartnerApiKey || '',
                label: 'Partner API Key',
                onConfirm: async ({ input }: { input?: string }) =>
                    store.saveUChatPartnerApiKey(input || ''),
            });

            if (result.confirmed) {
                sdk.showSuccessToast('UChat Partner API Key erfolgreich aktualisiert');
            } else if (result.error) {
                sdk.showErrorToast('Fehler beim Aktualisieren des UChat Partner API Keys');
            }
        };

        const handleChangeWorkspaceId = async () => {
            const result = await sdk.getDialogStringInput({
                title: 'UChat Workspace-ID eingeben',
                message: 'Bitte geben Sie Ihre UChat Workspace-ID ein',
                initialValue: store.uchatConfig.uchatWorkspaceId || '',
                label: 'Workspace-ID',
                onConfirm: async ({ input }: { input?: string }) =>
                    store.saveUChatWorkspaceId(input || ''),
            });

            if (result.confirmed) {
                sdk.showSuccessToast('UChat Workspace-ID erfolgreich aktualisiert');
            } else if (result.error) {
                sdk.showErrorToast('Fehler beim Aktualisieren der UChat Workspace-ID');
            }
        };

        const hasPartnerApiKey = store.hasUChatPartnerApiKey;
        const hasWorkspaceId = store.hasUChatWorkspaceId;
        const currentWorkspaceId = store?.uchatConfig?.uchatWorkspaceId || '';

        return (
            <Card
                title="UChat Partner API Konfiguration"
                style={{
                    marginBottom: '1rem',
                    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
                    borderRadius: '8px',
                }}
            >
                <div className="p-fluid">
                    {hasPartnerApiKey ? (
                        <div>
                            <Message
                                severity="success"
                                text="UChat Partner API-Schlüssel ist konfiguriert"
                                style={{ marginBottom: '1.5rem' }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1rem',
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    label="Partner API-Schlüssel ändern"
                                    icon="pi pi-pencil"
                                    onClick={handleChangePartnerApiKey}
                                    className="p-button-outlined"
                                />
                            </div>
                            <div
                                className="p-fluid"
                                style={{ marginTop: '1rem' }}
                            >
                                <div>
                                    <label
                                        style={{
                                            display: 'block',
                                            marginBottom: '0.5rem',
                                            fontWeight: 'bold',
                                        }}
                                    >
                                        Aktuelle Workspace-ID:
                                    </label>
                                    <p
                                        style={{
                                            margin: 0,
                                            padding: '0.5rem',
                                            backgroundColor: '#f8f9fa',
                                            borderRadius: '4px',
                                            fontSize: '1rem',
                                            color: currentWorkspaceId ? '#333' : '#666',
                                        }}
                                    >
                                        {currentWorkspaceId || 'Keine Workspace-ID konfiguriert'}
                                    </p>
                                </div>

                                {hasPartnerApiKey && !hasWorkspaceId && (
                                    <Message
                                        severity="warn"
                                        text="⚠️ Warnung: Eine Workspace-ID ist erforderlich bei Nutzung der Partner API"
                                        style={{ marginTop: '1rem', marginBottom: '1rem' }}
                                    />
                                )}

                                <div
                                    style={{
                                        display: 'flex',
                                        gap: '0.5rem',
                                        justifyContent: 'center',
                                        marginTop: '1.5rem',
                                    }}
                                >
                                    <Button
                                        label={
                                            currentWorkspaceId
                                                ? 'Workspace-ID ändern'
                                                : 'Workspace-ID hinzufügen'
                                        }
                                        icon={currentWorkspaceId ? 'pi pi-pencil' : 'pi pi-plus'}
                                        onClick={handleChangeWorkspaceId}
                                        className={
                                            currentWorkspaceId
                                                ? 'p-button-outlined'
                                                : 'p-button-primary'
                                        }
                                    />
                                </div>
                            </div>
                        </div>
                    ) : (
                        <div>
                            <Message
                                severity="warn"
                                text="Kein UChat Partner API-Schlüssel konfiguriert"
                                style={{ marginBottom: '1.5rem' }}
                            />
                            <div
                                style={{
                                    display: 'flex',
                                    flexDirection: 'column',
                                    gap: '1rem',
                                    alignItems: 'center',
                                }}
                            >
                                <Button
                                    label="Partner API-Schlüssel hinzufügen"
                                    icon="pi pi-plus"
                                    onClick={handleChangePartnerApiKey}
                                    className="p-button-primary"
                                />
                            </div>
                        </div>
                    )}
                </div>
            </Card>
        );
    }
);

const UChatSettings = observer(
    ({ store, sdk }: { store: SystemSettingStore; sdk: ApplicationSdk }) => {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '1rem' }}>
                <BotPhoneNumberSettings
                    store={store}
                    sdk={sdk}
                />
                <UChatApiKeySettings
                    store={store}
                    sdk={sdk}
                />
                <UChatPartnerApiKeySettings
                    store={store}
                    sdk={sdk}
                />
            </div>
        );
    }
);

export default UChatSettings;
